import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../services/api";
import { formatDate } from "../../lib/dateFormat";

/**
 * „N sportivi așteaptă să fie acceptați" — banner plus the dialog that lets
 * somebody act on them.
 *
 * A pending athlete already has an account and a profile, which is why they
 * appear on the roster; they occupy no seat, are billed nothing and sit on no
 * squad until somebody says yes. That makes a queue nobody notices exactly as
 * good as no gate at all, so this says itself out loud on the roster page.
 *
 * The count is read from the club, not from the filtered table: a search for
 * "Popescu" must not make four people waiting look like one.
 */

type JoinSource = {
  kind: "invitation" | "join_link" | "added_by_club" | "unknown";
  by_name: string | null;
  label: string | null;
};

type PendingRequest = {
  /** The membership id — what approve/reject take. */
  id: string;
  created_at: string;
  team: { id: string; name: string; age_group: string | null } | null;
  sport_name?: string | null;
  source?: JoinSource | null;
  athlete: {
    id: string;
    full_name: string | null;
    login: string;
    email?: string;
    phone?: string | null;
    age?: number | null;
    age_group: string | null;
    guardian: { name?: string; email?: string; phone?: string } | null;
    parents?: { id: string; full_name: string | null; email: string; phone: string | null }[];
  };
};

/** How somebody got here, in words rather than a code. */
function sourceLabel(source?: JoinSource | null): string {
  if (!source) return "sursă necunoscută";
  if (source.kind === "invitation") {
    return source.by_name ? `invitat de ${source.by_name}` : "invitat de club";
  }
  if (source.kind === "join_link") {
    const whose = source.by_name ? `linkul lui ${source.by_name}` : "link de înscriere";
    return source.label ? `${whose} (${source.label})` : whose;
  }
  if (source.kind === "added_by_club") {
    return source.by_name ? `adăugat de ${source.by_name}` : "adăugat de club";
  }
  return "sursă necunoscută";
}

export default function PendingAthletesBanner({
  clubId,
  onChanged,
}: {
  clubId: string | null | undefined;
  /** Called after an accept or a reject, so the page's roster can refetch. */
  onChanged?: () => void;
}) {
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      const { data } = await api.get<{ requests: PendingRequest[] }>(`/join-links/requests/${clubId}`);
      setRequests(data.requests || []);
    } catch {
      // Non-fatal: the roster below is still useful without this.
      setRequests([]);
    }
    setLoaded(true);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, action: "approve" | "reject") => {
    if (
      action === "reject" &&
      !window.confirm("Respingi cererea? Contul sportivului rămâne, doar nu intră în club.")
    ) {
      return;
    }
    setBusy(id);
    try {
      await api.post(`/join-links/requests/${id}/${action}`);
      toast.success(action === "approve" ? "Sportiv acceptat." : "Cerere respinsă.");
      const left = requests.filter((r) => r.id !== id);
      setRequests(left);
      if (left.length === 0) setOpen(false);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Acțiunea nu a reușit.");
      await load();
    }
    setBusy(null);
  };

  // Nothing to decide, nothing to say. A banner that reads "0 în așteptare" is
  // noise on every other day of the year.
  if (!loaded || requests.length === 0) return null;

  const n = requests.length;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 p-4 border shadow-sm rounded-2xl border-amber-200 bg-amber-50">
        <span className="flex items-center justify-center flex-shrink-0 w-10 h-10 font-bold rounded-xl bg-amber-100 text-amber-700">
          {n}
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-bold text-slate-800">
            {n === 1 ? "Un sportiv așteaptă să fie acceptat" : `${n} sportivi așteaptă să fie acceptați`}
          </p>
          <p className="text-xs text-slate-600">
            Au cont, dar nu sunt pe nicio echipă și nu sunt facturați până nu îi accepți.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="px-4 py-2 text-sm font-bold text-white rounded-lg bg-amber-600 hover:bg-amber-700"
        >
          Vezi și acceptă
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50">
          <div className="w-full max-w-2xl my-8 bg-white shadow-xl rounded-2xl">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-slate-800">Sportivi în așteptare ({n})</h3>
                <p className="text-xs text-slate-500">
                  Acceptarea îi pune pe echipa cerută și începe să conteze pentru abonament.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:bg-slate-100"
                aria-label="Închide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ul className="divide-y divide-slate-100 max-h-[65vh] overflow-y-auto">
              {requests.map((r) => {
                const parents = r.athlete.parents || [];
                return (
                  <li key={r.id} className="flex flex-wrap items-start gap-3 px-5 py-4">
                    <div className="flex-1 min-w-[240px]">
                      <Link
                        to={`/dashboard/admin-club/athletes/${r.athlete.id}`}
                        className="text-sm font-semibold text-slate-800 hover:text-indigo-600"
                      >
                        {r.athlete.full_name || r.athlete.login}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {[
                          r.athlete.age != null ? `${r.athlete.age} ani` : r.athlete.age_group,
                          r.team ? r.team.name : "fără echipă cerută",
                          r.sport_name,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="text-xs text-slate-500">{sourceLabel(r.source)}</p>

                      {/* Contact. The point of the list is that somebody can
                          ring the family before deciding. */}
                      {parents.length > 0 ? (
                        <ul className="mt-1 space-y-0.5">
                          {parents.map((p) => (
                            <li key={p.id} className="text-xs text-slate-500">
                              {p.full_name || p.email}
                              {p.phone && (
                                <>
                                  {" · "}
                                  <a href={`tel:${p.phone}`} className="text-indigo-600 hover:underline">
                                    {p.phone}
                                  </a>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : r.athlete.phone ? (
                        <p className="mt-1 text-xs">
                          <a href={`tel:${r.athlete.phone}`} className="text-indigo-600 hover:underline">
                            {r.athlete.phone}
                          </a>
                        </p>
                      ) : r.athlete.guardian?.name || r.athlete.guardian?.phone ? (
                        <p className="mt-1 text-xs text-slate-500">
                          {r.athlete.guardian?.name}
                          {r.athlete.guardian?.phone ? ` · ${r.athlete.guardian.phone}` : ""}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-slate-400">
                          fără telefon și fără părinte legat
                        </p>
                      )}

                      <p className="mt-1 text-[11px] text-slate-400">
                        cerere din {formatDate(r.created_at)}
                      </p>
                    </div>

                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => act(r.id, "reject")}
                        disabled={busy === r.id}
                        className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Respinge
                      </button>
                      <button
                        type="button"
                        onClick={() => act(r.id, "approve")}
                        disabled={busy === r.id}
                        className="px-3 py-1.5 text-xs font-bold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Acceptă
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="flex justify-end px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Închide
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
